import { useCallback } from "react";
import FeedIcon from "../../../../assets/images/manage-participant/activities-feed.svg";
import FileIcon from "../../../../assets/images/manage-participant/assessment-file.svg";
import CalendarIcon from "../../../../assets/images/manage-participant/event-calendar.svg";
import ProfileAvatar from "../../../../assets/images/manage-participant/profile-avatar.svg";
import { MuiBox, MuiGrid, MuiTypography } from "../../../../components";
import { DateTimeChip } from "../../../../components/dateTimeChip/DateTimeChip";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  gridSize3Column,
} from "../../../../helpers/constants";
import { isNotEmpty } from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ParticipantDetailsCardTypes } from "../../modelsAndSchemas/Models";
import { manageParticipantHeaderSiteCoreKeys } from "../../sitecore/SiteCoreKeys";
import { ManageParticipantsInfoCard } from "./components/ManageParticipantInfoCard";
import { ManageParticipantsInfoStyle } from "./ManageParticipantsInfo.style";

interface ManageParticipantsInfoProps {
  onCardClick: (type: ParticipantDetailsCardTypes) => void;
  participantDetails:
    | {
        activitiesCount: number;
        clientName: string;
        clientCode: string;
        participantName: string;
        assessmentCount: number;
        sessionAndEventsCount: number;
        programStatusId: StatusBadgeType;
        nextContactDate?: string;
      }
    | undefined;
}
export const ManageParticipantsInfo = ({
  onCardClick,
  participantDetails,
}: ManageParticipantsInfoProps) => {
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { isTablet } = useMediaQueries();

  const getLabel = useCallback(
    (childKey: string) => {
      return fetchLabel(
        manageParticipantHeaderSiteCoreKeys.manageParticipantHeaderTitle,
        childKey,
      );
    },
    [fetchLabel],
  );

  return (
    <ManageParticipantsInfoStyle>
      <MuiBox className="bg">
        <MuiBox className="participant-avatar-container">
          <img src={ProfileAvatar} className="participant-avatar" alt="" />
          <MuiBox className="participant-avatar-status">
            {participantDetails?.programStatusId && (
              <StatusBadge
                label={fetchValueByKeyOfDropdown(
                  manageParticipantHeaderSiteCoreKeys.manageParticipantHeaderTitle,
                  manageParticipantHeaderSiteCoreKeys.programStatusList,
                  participantDetails.programStatusId,
                )}
                status={participantDetails?.programStatusId}
                data-testid="participant-status-badge"
              />
            )}
          </MuiBox>
        </MuiBox>
      </MuiBox>
      <MuiBox className="participant-container">
        <MuiEllipseLabel
          labelTypoVariant="h2SemiBold"
          text={participantDetails?.participantName}
          data-testid="participant-name"
        />
        <MuiBox className="participant-info">
          <MuiBox>
            <MuiBox className="participant-client-info">
              <MuiEllipseLabel
                labelTypoVariant="h3"
                text={participantDetails?.clientName}
                data-testid="participant-client-name"
              />
              <MuiTypography
                variant="h3SemiBold"
                data-testid="participant-client-code"
              >
                {participantDetails?.clientCode}
              </MuiTypography>
            </MuiBox>
          </MuiBox>

          <MuiBox className="participant-next-session">
            <MuiTypography variant="h3" data-testid="next-contact-date-label">
              {getLabel(manageParticipantHeaderSiteCoreKeys.nextContactDate)}
            </MuiTypography>

            {participantDetails?.nextContactDate &&
            isNotEmpty(participantDetails?.nextContactDate) ? (
              <DateTimeChip
                timestamp={participantDetails?.nextContactDate}
                data-testid="next-contact-date-chip"
              />
            ) : (
              <MuiTypography
                variant="h5SemiBold"
                className="align-center"
                data-testid="next-contact-date-dash"
              >
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            )}
          </MuiBox>
        </MuiBox>
        <MuiBox className="participant-divider"></MuiBox>
        <MuiGrid
          container
          className="cards-container"
          columnSpacing={5}
          rowSpacing={isTablet ? 3 : 0}
        >
          <MuiGrid size={gridSize3Column}>
            <ManageParticipantsInfoCard
              count={participantDetails?.activitiesCount ?? 0}
              header={getLabel(
                manageParticipantHeaderSiteCoreKeys.activitiesTile,
              )}
              icon={FeedIcon}
              label={getLabel(
                manageParticipantHeaderSiteCoreKeys.activitiesTilePerformed,
              )}
              onCardClick={() => onCardClick("activities")}
              type="activities"
            />
          </MuiGrid>
          <MuiGrid size={gridSize3Column}>
            <ManageParticipantsInfoCard
              count={participantDetails?.sessionAndEventsCount ?? 0}
              header={getLabel(
                manageParticipantHeaderSiteCoreKeys.sessionsAndEventsTile,
              )}
              icon={CalendarIcon}
              label={getLabel(
                manageParticipantHeaderSiteCoreKeys.sessionsAndEventsTileAttended,
              )}
              onCardClick={() => onCardClick("events")}
              type="events"
            />
          </MuiGrid>
          <MuiGrid size={gridSize3Column}>
            <ManageParticipantsInfoCard
              count={participantDetails?.assessmentCount ?? 0}
              header={getLabel(
                manageParticipantHeaderSiteCoreKeys.assessmentsTile,
              )}
              icon={FileIcon}
              label={getLabel(
                manageParticipantHeaderSiteCoreKeys.assessmentsTileCompleted,
              )}
              onCardClick={() => onCardClick("assessments")}
              type="assessments"
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </ManageParticipantsInfoStyle>
  );
};
